import { get } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import type { ContentValue } from '@glint/template';

interface RecordSelectRowModel {
  [key: string]: ContentValue;
  id: string | number;
}

interface RecordSelectRowSignature {
  Args: {
    fields?: { name: string }[];
    model: RecordSelectRowModel;
    onDeselect: (model: RecordSelectRowModel) => void;
    onSelect: (model: RecordSelectRowModel) => void;
    // When provided, must be a tracked-aware array (e.g. Ember `A()` MutableArray
    // or `TrackedArray`). The parent (record-select/table) mutates via `addObject` /
    // `removeObject` / `clear` in place rather than replacing the reference, so
    // `isSelected` relies on the array proxy's tag invalidation to re-run. A plain
    // `[]` passed by a future caller would silently fail to re-render on selection
    // changes. Optional because the sole caller (`body/template.hbs`) is currently
    // `@glint-nocheck` and cannot enforce presence at the type layer.
    selectedModels?: RecordSelectRowModel[];
  };
  Element: HTMLTableRowElement;
}

export default class RecordSelectRow extends Component<RecordSelectRowSignature> {
  get isSelected(): boolean {
    return (this.args.selectedModels ?? []).includes(this.args.model);
  }

  handleInput = (event: Event): void => {
    const target = event.currentTarget as HTMLInputElement;
    if (target.checked) {
      this.args.onSelect(this.args.model);
    } else {
      this.args.onDeselect(this.args.model);
    }
  };

  <template>
    <tr ...attributes>
      <td class="align-middle pt-4 pr-2 pb-4 pl-2">
        <input
          type="checkbox"
          class="mx-1"
          aria-label="Select row {{@model.id}}"
          checked={{this.isSelected}}
          value={{@model.id}}
          {{on "input" this.handleInput}}
        />
      </td>
      {{#each @fields as |field|}}
        <td class="align-middle pt-4 pr-2 pb-4 pl-2">{{get @model field.name}}</td>
      {{/each}}
    </tr>
  </template>
}
